import { z } from 'zod';
import { attachmentIdsSchema, attachmentSchema } from '../constants/attachments.js';
import { ROLE_KEYS } from '../constants/permissions.js';
import { WIKI_PAGE_CONTENT_MAX, WIKI_SECTION_VISIBILITIES, WIKI_SLUG_PATTERN, } from '../constants/wiki.js';
import { emailSchema, paginationQuerySchema, patchableSchema, uuidSchema } from './common.js';
export const wikiSlugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(1)
    .max(120)
    .regex(WIKI_SLUG_PATTERN, 'Use lowercase letters, numbers and hyphens only');
// ------------------------------------------------------------------ sections
export const createWikiSectionSchema = z.object({
    name: z.string().trim().min(1).max(120),
    /** Defaults to a slug of the name when omitted — see `slugify` in
     *  `constants/wiki.ts`. */
    slug: wikiSlugSchema.optional(),
    description: z.string().trim().max(500).nullable().optional(),
    /** The section-level publish switch. A page's own `isPublished` still has
     *  to be set independently — see `wikiPageSchema` — so flipping a section
     *  to `internal` instantly hides every page in it, and flipping it to
     *  `public` does not itself publish anything. */
    visibility: z.enum(WIKI_SECTION_VISIBILITIES).default('internal'),
});
export const updateWikiSectionSchema = patchableSchema(createWikiSectionSchema);
export const wikiSectionSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    slug: z.string(),
    description: z.string().nullable(),
    visibility: z.enum(WIKI_SECTION_VISIBILITIES),
    sortOrder: z.number().int(),
    pageCount: z.number().int().nonnegative(),
    publishedPageCount: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const reorderWikiSectionsSchema = z.object({
    ids: z.array(uuidSchema).min(1).max(500),
});
// --------------------------------------------------------------------- pages
export const createWikiPageSchema = z.object({
    sectionId: uuidSchema,
    title: z.string().trim().min(1).max(200),
    slug: wikiSlugSchema.optional(),
    contentHtml: z.string().max(WIKI_PAGE_CONTENT_MAX).default(''),
    /** Files attached to the page (t1249); on update the full list. */
    attachmentIds: attachmentIdsSchema.optional(),
});
/** Publishing is deliberately not part of this patch — it always goes
 *  through `POST /wiki/pages/:id/publish`, which is `wiki:manage`-guarded
 *  rather than `wiki:write`, since it decides what the public site shows. */
export const updateWikiPageSchema = patchableSchema(createWikiPageSchema);
export const wikiPageListItemSchema = z.object({
    id: uuidSchema,
    sectionId: uuidSchema,
    title: z.string(),
    slug: z.string(),
    isPublished: z.boolean(),
    /** Page-level access lock (t0932) — see `wikiPageAccessSchema` for who can
     *  open it. Independent of `isPublished` and the section's `visibility`. */
    restricted: z.boolean(),
    sortOrder: z.number().int(),
    updatedByName: z.string().nullable(),
    updatedAt: z.iso.datetime(),
});
export const wikiPageSchema = wikiPageListItemSchema.extend({
    contentHtml: z.string(),
    attachments: z.array(attachmentSchema).default([]),
    createdAt: z.iso.datetime(),
});
export const wikiPagesQuerySchema = paginationQuerySchema.extend({
    sectionId: uuidSchema.optional(),
    published: z.coerce.boolean().optional(),
});
export const setWikiPagePublishedSchema = z.object({ published: z.boolean() });
export const reorderWikiPagesSchema = z.object({
    sectionId: uuidSchema,
    ids: z.array(uuidSchema).min(1).max(1000),
});
// ------------------------------------------------------------------- guests
/**
 * An invited outside collaborator — a `guest`-role user (see `ROLE_KEYS`)
 * whose wiki access is narrowed to the sections listed here, rather than the
 * whole internal wiki. See `modules/wiki/service.ts` for enforcement.
 */
export const wikiGuestSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    name: z.string(),
    isActive: z.boolean(),
    lastLoginAt: z.iso.datetime().nullable(),
    sectionIds: z.array(uuidSchema),
    createdAt: z.iso.datetime(),
});
export const createWikiGuestSchema = z.object({
    email: emailSchema,
    name: z.string().trim().min(1).max(150),
    sectionIds: z.array(uuidSchema).default([]),
});
export const setWikiGuestSectionsSchema = z.object({
    sectionIds: z.array(uuidSchema),
});
// --------------------------------------------------------- public knowledge base
/** What the public site is allowed to see of a section — no visibility flag,
 *  no id beyond what routing needs: there is nothing here for an internal
 *  reader to distinguish from a guest-restricted one. */
export const kbSectionSummarySchema = z.object({
    slug: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    pages: z.array(z.object({ slug: z.string(), title: z.string() })),
});
export const kbPageSchema = z.object({
    slug: z.string(),
    title: z.string(),
    contentHtml: z.string(),
    attachments: z.array(attachmentSchema).default([]),
    section: z.object({ slug: z.string(), name: z.string() }),
    updatedAt: z.iso.datetime(),
});
// ------------------------------------------------------------- page access (t0932)
/** Non-guest role a page grant or candidate list can target. Granting the
 *  `guest` role itself would open a restricted page to every invited guest —
 *  see `setWikiPageAccessSchema` and `WikiService.setPageAccess`. */
const GRANTABLE_ROLE_KEYS = ROLE_KEYS.filter((key) => key !== 'guest');
const grantableRoleKeySchema = z.enum(GRANTABLE_ROLE_KEYS);
export const wikiShareLinkSchema = z.object({
    id: uuidSchema,
    label: z.string().nullable(),
    hasPassword: z.boolean(),
    /** Derived server-side from `revokedAt`/`expiresAt` — never stored. */
    status: z.enum(['active', 'expired', 'revoked']),
    expiresAt: z.iso.datetime().nullable(),
    revokedAt: z.iso.datetime().nullable(),
    lastAccessedAt: z.iso.datetime().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
/**
 * Who can open a restricted page, and every share link ever issued for it —
 * the "Access" card on the page editor reads and writes this as one unit
 * (see `setWikiPageAccessSchema`).
 */
export const wikiPageAccessSchema = z.object({
    restricted: z.boolean(),
    users: z.array(z.object({
        userId: uuidSchema,
        name: z.string(),
        email: z.string().nullable(),
        roleKey: z.enum(ROLE_KEYS),
    })),
    roles: z.array(z.object({ roleId: uuidSchema, key: z.enum(ROLE_KEYS), name: z.string() })),
    links: z.array(wikiShareLinkSchema),
});
/** Replace-style write, same shape as `setWikiGuestSectionsSchema` — the
 *  server deletes and reinserts both grant tables in one transaction. */
export const setWikiPageAccessSchema = z.object({
    restricted: z.boolean(),
    userIds: z.array(uuidSchema).max(200).default([]),
    roleIds: z.array(uuidSchema).max(20).default([]),
});
export const createWikiShareLinkSchema = z.object({
    label: z.string().trim().min(1).max(120).optional(),
    /** Omitted = an open link; anyone with the URL can view the page. */
    password: z.string().min(6).max(200).optional(),
    /** Omitted = never expires on its own; revoke it by hand instead. Must be
     *  in the future — checked server-side. */
    expiresAt: z.iso.datetime().optional(),
});
export const createdWikiShareLinkSchema = z.object({
    link: wikiShareLinkSchema,
    /** The full shareable URL, token included. Shown to the admin exactly
     *  once — the server never returns the raw token again after this. */
    url: z.string(),
});
/** Everyone a page grant could plausibly target: active staff and guests
 *  together (both are just role-holding `users` rows), plus the grantable
 *  roles. Guests are distinguished in the UI by `roleKey === 'guest'`. */
export const wikiAccessCandidatesSchema = z.object({
    users: z.array(z.object({ id: uuidSchema, name: z.string(), roleKey: z.enum(ROLE_KEYS) })),
    roles: z.array(z.object({ id: uuidSchema, key: grantableRoleKeySchema, name: z.string() })),
});
export const resolveSharedWikiPageSchema = z.object({
    token: z.string().min(20).max(200),
    password: z.string().max(200).optional(),
});
/** Same discriminated-union trick as login's `email_code_required` — a 200
 *  either way, since "does a password prompt exist" is the only thing this
 *  is allowed to reveal about an unauthenticated request. */
export const sharedWikiPageResultSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('ok'),
        page: z.object({
            title: z.string(),
            contentHtml: z.string(),
            attachments: z.array(attachmentSchema).default([]),
            updatedAt: z.iso.datetime(),
        }),
    }),
    z.object({ status: z.literal('password_required') }),
]);
//# sourceMappingURL=wiki.js.map